import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import {
  AlertCircle,
  ChevronDown,
  Download,
  History,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import {
  type DetailContext,
  type FieldDef,
  type FilterDef,
  type FilterValues,
  type ResourceConfig,
  ResourcePage,
  csvName,
  fmtDate,
  isSet,
} from "@/components/resource-page";
import { BackArrow, backLinkClass, PageHeader } from "@/components/ui/page";
import { Badge } from "@/components/ui/badge";
import { Tabs } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { menuItemClass } from "@/components/ui/menu";
import { Checkbox, Field, Input } from "@/components/ui/field";
import { FkSelect } from "@/components/ui/fk-select";
import { OptionSelect } from "@/components/ui/option-select";
import { type CellKind, CsvEditor, type RowError, pageSlice } from "@/components/csv-editor";
import { api, errorMessage } from "@/services/api";
import { useCan } from "@/utils/permissions";
import type { ListParams } from "@/utils/geo";
import { cn } from "@/utils/utils";
import { columnKey, missingColumns, parseCsv, toCsv } from "@/utils/csv";
import { StreamError, postEventStream } from "@/services/sse";
import type { ProblemDetail } from "@/meta/types";
import { birthDateMax, birthDateMin } from "@/utils/dates";
import type { Translate, TranslationKey } from "@/constants/i18n";
import { useTranslation } from "@/store/locale";
import { MemberDetail } from "@/pages/MemberDetailPage";

/** One remembered search, as the API returns it. */
interface SearchTerm {
  id: number;
  term: string;
  updated_at: string;
}

export interface Member {
  id: number;
  account_number: string;
  membership_type: string;
  prefix: string | null;
  suffix: string | null;
  first_name: string;
  middle_name: string | null;
  last_name: string | null;
  gender: string | null;
  date_of_birth: string | null;
  snr_account_number: string | null;
  account_type: string;
  account_status: string;
  parent_account_number: string | null;
  supplementary_account_number: string | null;
  /** Counted by the API on the detail; a list row does not carry it. */
  has_supplementary_created: boolean;
  family_member_account_number: string | null;
  employee_number: string | null;
  employee_relation: string | null;
  acquisition_type: string | null;
  membership_tier: string | null;
  amendment: string | null;
  volume_shopper: boolean;
  co_brand_tagging: string | null;
  card_name: string | null;
  card_number: string | null;
  temporary_card_number: string | null;
  issued_date: string | null;
  valid_until: string | null;
  no_expiry: boolean;
  is_print_ready: boolean;
  union_bank_tag: string | null;
  is_temporarily_active: boolean;
  extended_time_minutes: number | null;
  signup_club_id: number | null;
  signup_club_name: string | null;
  active_club_id: number | null;
  active_club_name: string | null;
  renew_club_id: number | null;
  renew_club_name: string | null;
  city_id: number | null;
  city_name: string | null;
  barangay_id: number | null;
  barangay_name: string | null;
  address_street: string | null;
  unit_number: string | null;
  building_name: string | null;
  mobile_number: string | null;
  email: string | null;
  phone_consent: boolean;
  email_consent: boolean;
  only_important_updates: boolean;
  business_name: string | null;
  business_type: string | null;
  business_company_name: string | null;
  company_name: string | null;
  position: string | null;
  id_card_image: string | null;
  profile_image: string | null;
  created_by_id: number | null;
  created_by_name: string | null;
  updated_by_id: number | null;
  updated_by_name: string | null;
  created_at: string;
  updated_at: string;
}

type Opt = { value: string; label: string };
const opts = (t: Translate, pairs: [string, TranslationKey][]): Opt[] =>
  pairs.map(([value, label]) => ({ value, label: t(label) }));

export const PREFIX = (t: Translate) =>
  opts(t, [
    ["mr", "mem.mr"],
    ["ms", "mem.ms"],
    ["mrs", "mem.mrs"],
    ["dr", "mem.dr"],
    ["atty", "mem.atty"],
    ["engr", "mem.engr"],
  ]);
// Suffixes read the same in every language.
export const SUFFIX = (): Opt[] =>
  ["Sr", "Jr", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"].map((v) => ({
    value: v.toLowerCase(),
    label: v,
  }));
export const GENDER = (t: Translate) =>
  opts(t, [
    ["female", "mem.female"],
    ["male", "mem.male"],
    ["other", "mem.other"],
    ["undisclosed", "mem.undisclosed"],
  ]);
export const ACQUISITION = (t: Translate) =>
  opts(t, [
    ["walk_in", "mem.walkIn"],
    ["canvassing", "mem.canvassing"],
    ["benefits", "mem.benefits"],
  ]);
/** What was bought. Only a member-tier account has one that means anything. */
export const MEMBERSHIP_TYPE = (t: Translate) =>
  opts(t, [
    ["gold", "mem.gold"],
    ["business", "mem.business"],
    ["unioil", "mem.unioil"],
    ["qsr", "mem.qsr"],
  ]);
/** Who the member is to S&R. With the account type it decides the card number. */
export const TIER = (t: Translate) =>
  opts(t, [
    ["member", "mem.member"],
    ["affiliates", "mem.affiliates"],
    ["employee", "mem.employee"],
    ["complimentary", "mem.complimentary"],
  ]);
export const ACCOUNT_TYPE = (t: Translate) =>
  opts(t, [
    ["primary", "mem.primary"],
    ["supplementary", "mem.supplementary"],
  ]);
/** Where a member's co-brand (UnionBank) application stands. */
export const CO_BRAND = (t: Translate) =>
  opts(t, [
    ["approved", "mem.approved"],
    ["declined", "mem.declined"],
    ["pending", "mem.pending"],
  ]);
export const STATUS = (t: Translate) =>
  opts(t, [
    ["active", "mem.active"],
    ["expired", "mem.expired"],
    ["cancelled", "mem.cancelled"],
    ["blacklisted", "mem.blacklisted"],
  ]);
const RELATION = (t: Translate) =>
  opts(t, [
    ["spouse", "mem.spouse"],
    ["child", "mem.child"],
    ["parent", "mem.parent"],
    ["sibling", "mem.sibling"],
    ["other", "mem.other"],
  ]);

export const label = (options: Opt[], value: string | null) =>
  options.find((o) => o.value === value)?.label ?? value ?? "—";
export const day = (value: string | null) =>
  value ? format(new Date(value), "MM/dd/yyyy") : "—";

/** The name as one line. There is no full_name column: the parts are the name. */
export const fullName = (m: {
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
}) => [m.first_name, m.middle_name, m.last_name].filter(Boolean).join(" ");

// ---------------------------------------------------------------------------
// The form: every field from the design, grouped the way the screen groups them.
// ---------------------------------------------------------------------------
export const fields = (t: Translate): FieldDef[] => [
  {
    section: t("mem.sectionIdentity"),
    name: "prefix",
    label: t("mem.prefix"),
    type: "select",
    options: PREFIX(t),
  },
  {
    section: t("mem.sectionIdentity"),
    name: "suffix",
    label: t("mem.suffix"),
    type: "select",
    options: SUFFIX(),
  },
  {
    section: t("mem.sectionIdentity"),
    name: "first_name",
    label: t("mem.firstName"),
    required: true,
  },
  { section: t("mem.sectionIdentity"), name: "middle_name", label: t("mem.middleName") },
  { section: t("mem.sectionIdentity"), name: "last_name", label: t("mem.lastName") },
  {
    section: t("mem.sectionIdentity"),
    name: "gender",
    label: t("mem.gender"),
    required: true,
    type: "select",
    options: GENDER(t),
  },
  {
    section: t("mem.sectionIdentity"),
    name: "date_of_birth",
    label: t("mem.dateOfBirth"),
    required: true,
    type: "date",
    // The same bounds the API enforces, so the picker cannot offer a refusal.
    min: birthDateMin(),
    max: birthDateMax(),
  },

  {
    section: t("mem.sectionAccount"),
    name: "account_number",
    label: t("mem.accNo"),
    required: true,
    lockOnEdit: true,
  },
  {
    section: t("mem.sectionAccount"),
    name: "snr_account_number",
    label: t("mem.snrAccountNumber"),
  },
  {
    section: t("mem.sectionAccount"),
    name: "account_type",
    label: t("mem.accountType"),
    type: "select",
    options: ACCOUNT_TYPE(t),
    lockOnEdit: true,
  },
  {
    section: t("mem.sectionAccount"),
    name: "account_status",
    label: t("mem.accountStatus"),
    type: "select",
    options: STATUS(t),
  },
  {
    section: t("mem.sectionAccount"),
    name: "membership_type",
    label: t("mem.membershipType"),
    type: "select",
    options: MEMBERSHIP_TYPE(t),
    required: true,
  },
  {
    section: t("mem.sectionAccount"),
    name: "membership_tier",
    label: t("mem.membershipTier"),
    required: true,
    type: "select",
    options: TIER(t),
  },
  {
    section: t("mem.sectionAccount"),
    name: "acquisition_type",
    label: t("mem.acquisitionType"),
    required: true,
    type: "select",
    options: ACQUISITION(t),
  },
  {
    section: t("mem.sectionAccount"),
    name: "amendment",
    label: t("mem.amendment"),
    disabled: true,
  },
  {
    section: t("mem.sectionAccount"),
    name: "parent_account_number",
    label: t("mem.parentAccount"),
    lockOnEdit: true,
  },
  {
    section: t("mem.sectionAccount"),
    name: "supplementary_account_number",
    label: t("mem.supplementaryAccount"),
    disabled: true,
  },
  {
    section: t("mem.sectionAccount"),
    name: "family_member_account_number",
    label: t("mem.familyMember"),
  },
  { section: t("mem.sectionAccount"), name: "employee_number", label: t("mem.employeeNumber") },
  {
    section: t("mem.sectionAccount"),
    name: "employee_relation",
    label: t("mem.employeeRelation"),
    type: "select",
    options: RELATION(t),
  },
  {
    section: t("mem.sectionAccount"),
    name: "volume_shopper",
    label: t("mem.volumeShopper"),
    type: "boolean",
  },
  {
    section: t("mem.sectionAccount"),
    name: "co_brand_tagging",
    label: t("mem.coBrandTagging"),
  },
  {
    section: t("mem.sectionAccount"),
    name: "union_bank_tag",
    label: t("mem.unionBankTag"),
    type: "select",
    options: CO_BRAND(t),
  },

  { section: t("mem.sectionCard"), name: "card_name", label: t("mem.cardName") },
  { section: t("mem.sectionCard"), name: "card_number", label: t("mem.cardNumber") },
  {
    section: t("mem.sectionCard"),
    name: "temporary_card_number",
    label: t("mem.temporaryCardNumber"),
  },
  {
    section: t("mem.sectionCard"),
    name: "issued_date",
    label: t("mem.issuedDate"),
    type: "date",
  },
  {
    section: t("mem.sectionCard"),
    name: "valid_until",
    label: t("mem.validUntil"),
    type: "date",
  },
  {
    section: t("mem.sectionCard"),
    name: "no_expiry",
    label: t("mem.noExpiry"),
    type: "boolean",
  },
  {
    section: t("mem.sectionCard"),
    name: "is_print_ready",
    label: t("mem.isPrintReady"),
    type: "boolean",
  },
  {
    section: t("mem.sectionCard"),
    name: "is_temporarily_active",
    label: t("mem.temporarilyActive"),
    type: "boolean",
  },
  {
    section: t("mem.sectionCard"),
    name: "extended_time_minutes",
    label: t("mem.extendedTime"),
    type: "number",
    min: 0,
  },

  {
    section: t("mem.sectionClubsAddress"),
    name: "signup_club_id",
    label: t("mem.signupClub"),
    type: "fk",
    endpoint: "/clubs/compact",
  },
  {
    section: t("mem.sectionClubsAddress"),
    name: "active_club_id",
    label: t("mem.activeClub"),
    type: "fk",
    endpoint: "/clubs/compact",
  },
  {
    section: t("mem.sectionClubsAddress"),
    name: "renew_club_id",
    label: t("mem.renewClub"),
    type: "fk",
    endpoint: "/clubs/compact",
  },
  {
    section: t("mem.sectionClubsAddress"),
    name: "city_id",
    label: t("mem.city"),
    type: "fk",
    endpoint: "/cities/compact",
  },
  {
    section: t("mem.sectionClubsAddress"),
    name: "barangay_id",
    label: t("mem.barangay"),
    type: "fk",
    endpoint: "/barangays/compact",
    dependsOn: "city_id",
  },
  {
    section: t("mem.sectionClubsAddress"),
    name: "address_street",
    label: t("mem.addressStreet"),
  },
  { section: t("mem.sectionClubsAddress"), name: "unit_number", label: t("mem.unitNumber") },
  {
    section: t("mem.sectionClubsAddress"),
    name: "building_name",
    label: t("mem.buildingName"),
  },

  {
    section: t("mem.sectionContactConsent"),
    name: "mobile_number",
    label: t("mem.mobileNumber"),
    type: "tel",
  },
  {
    section: t("mem.sectionContactConsent"),
    name: "email",
    label: t("mem.email"),
    type: "email",
  },
  {
    section: t("mem.sectionContactConsent"),
    name: "phone_consent",
    label: t("mem.phoneConsent"),
    type: "boolean",
  },
  {
    section: t("mem.sectionContactConsent"),
    name: "email_consent",
    label: t("mem.emailConsent"),
    type: "boolean",
  },
  {
    section: t("mem.sectionContactConsent"),
    name: "only_important_updates",
    label: t("mem.onlyImportantUpdates"),
    type: "boolean",
  },

  { section: t("mem.sectionBusiness"), name: "business_name", label: t("mem.businessName") },
  { section: t("mem.sectionBusiness"), name: "business_type", label: t("mem.businessType") },
  {
    section: t("mem.sectionBusiness"),
    name: "business_company_name",
    label: t("mem.businessCompanyName"),
  },
  { section: t("mem.sectionBusiness"), name: "company_name", label: t("mem.companyName") },
  { section: t("mem.sectionBusiness"), name: "position", label: t("mem.position") },

  {
    section: t("mem.sectionImages"),
    name: "id_card_image",
    label: t("mem.idCard"),
    type: "image",
  },
  {
    section: t("mem.sectionImages"),
    name: "profile_image",
    label: t("mem.profileImage"),
    type: "image",
  },
];

const FIELD_NAMES = fields((key) => key).map((f) => f.name);
export const toForm = (m: Member): Record<string, string> =>
  Object.fromEntries(
    FIELD_NAMES.map((name) => {
      const value = (m as unknown as Record<string, unknown>)[name];
      return [name, value === null || value === undefined ? "" : String(value)];
    }),
  );

const build = (t: Translate): ResourceConfig<Member> => ({
  title: t("nav.members"),
  singular: t("mem.singular"),
  endpoint: "/members",
  model: "member",
  // Names, and the three numbers the drawer therefore does not ask for.
  searchPlaceholder: t("mem.searchPlaceholder"),
  // Newest first, off the primary key. `created_at` says the same thing a
  // beat slower — 34ms against 18ms on forty million rows — and needs its own
  // index to say it; `id` is the key every partition is already sorted by.
  defaultSort: { key: "id", order: "desc" },
  searchHistory: true,
  // Tens of millions of rows: a one- or two-letter prefix is real work for a
  // useless answer, so the box waits for the third character. Matches
  // SEARCH_MIN_LENGTH, which the API enforces.
  searchMinLength: 3,
  // Typing costs nothing: the suggestions come from the copy of the history the
  // browser holds, matched locally. The query over thirty million members runs
  // when someone settles on a term — Enter, or picking a suggestion.
  searchOnCommit: true,
  // Recent searches follow the account, not the browser.
  // The whole list once, then held in the browser and matched there; every
  // read after that asks only for what changed. A keystroke costs nothing;
  // only a committed term costs a request, and that one answers with whatever
  // else changed, so the copy stays in step.
  searchHistoryStore: {
    load: async (since, signal) =>
      (
        await api.get<SearchTerm[]>("/members/search-history", {
          ...(since ? { params: { since } } : {}),
          ...(signal ? { signal } : {}),
        })
      ).data,
    remember: async (term, since) =>
      (await api.post<SearchTerm[]>("/members/search-history", { term, since })).data,
    forget: (id) => api.delete(`/members/search-history/${id}`),
  },
  // Downloads are `member_<date>.csv`, the same name the server's export uses.
  csvBase: "member",
  // The filters go as a body: no query-string limit, lists carried as lists.
  listMethod: "post",
  // Thirty million rows are not counted for every page; past this it reads "100,000+".
  // The same number as `COUNT_CAP` in the members service, and it has to stay
  // that way: it is what decides whether a total is shown as a floor. Lower
  // than the server's and an exact total reads as "99,999+"; higher and a
  // floor reads as exact.
  countCap: 100_000,
  filterLabel: t("mem.advancedFilter"),
  projectFields: true,
  viewColumn: "last_name",
  noRowMenu: true,
  // A membership is a record of something that happened; nothing deletes one.
  noDelete: true,
  // The whole page, like every other record — not a sheet over the list. The
  // id is in the address bar, obfuscated, so a reload comes back here.
  renderView: (m, ctx) => <MemberView member={m} ctx={ctx} />,
  exportable: true,
  formColumns: 2,
  addLabel: t("mem.addMember"),
  // Just the primary action. `CsvMenus` is the `toolbar` below, and having it
  // here as well drew Batch Upload and Batch Update twice in the same row.
  addMenu: <AddMemberMenu />,
  hiddenByDefault: [
    "mobile_number",
    "created_at",
    "suffix",
    "employee_number",
    "parent_account_number",
    "co_brand_tagging",
    "created_by_id",
    "updated_by_id",
    "updated_at",
    "signup_club",
    "prefix",
    "gender",
    "city",
    "barangay",
    "email_consent",
    "account_type",
  ],
  columns: [
    {
      key: "last_name",
      fields: ["first_name", "middle_name", "last_name"],
      label: t("mem.fullName"),
      sortKey: "last_name",
      // One line, whatever the name. A four-word membership name wrapped to
      // three lines and took the whole row with it, so a page of members was
      // read at three different row heights.
      className: "max-w-64",
      render: (m) => (
        <span className="block truncate underline underline-offset-4" title={fullName(m)}>
          {fullName(m)}
        </span>
      ),
    },
    {
      key: "date_of_birth",
      label: t("mem.dateOfBirth"),
      sortKey: "date_of_birth",
      render: (m) => day(m.date_of_birth),
    },
    {
      key: "mobile_number",
      label: t("mem.mobileNumber"),
      render: (m) => m.mobile_number ?? "—",
    },
    {
      key: "account_number",
      label: t("mem.accountNumber"),
      sortKey: "account_number",
      render: (m) => m.account_number,
    },
    {
      key: "membership_type",
      label: t("mem.membershipType"),
      render: (m) => label(MEMBERSHIP_TYPE(t), m.membership_type),
    },
    {
      key: "membership_tier",
      label: t("mem.membershipTier"),
      render: (m) => label(TIER(t), m.membership_tier),
    },
    {
      key: "card_number",
      label: t("mem.cardNumber"),
      sortKey: "card_number",
      render: (m) => m.card_number ?? "—",
    },
    {
      key: "valid_until",
      label: t("mem.validUntil"),
      sortKey: "valid_until",
      render: (m) => day(m.valid_until),
    },
    {
      key: "account_status",
      label: t("mem.accountStatus"),
      render: (m) => (
        <Badge tone={m.account_status === "active" ? "success" : "neutral"}>
          {label(STATUS(t), m.account_status)}
        </Badge>
      ),
      csv: (m) => m.account_status,
    },
    {
      key: "account_type",
      label: t("mem.accountType"),
      render: (m) => label(ACCOUNT_TYPE(t), m.account_type),
    },
    {
      key: "signup_club",
      fields: ["signup_club_name"],
      label: t("mem.signUpClub"),
      render: (m) => m.signup_club_name ?? "—",
      csv: (m) => m.signup_club_name,
    },
    { key: "prefix", label: t("mem.prefix"), render: (m) => label(PREFIX(t), m.prefix) },
    { key: "gender", label: t("mem.gender"), render: (m) => label(GENDER(t), m.gender) },
    {
      key: "city",
      label: t("mem.city"),
      fields: ["city_name"],
      render: (m) => m.city_name ?? "—",
      csv: (m) => m.city_name,
    },
    {
      key: "barangay",
      fields: ["barangay_name"],
      label: t("mem.barangay"),
      render: (m) => m.barangay_name ?? "—",
      csv: (m) => m.barangay_name,
    },
    {
      key: "active_club",
      fields: ["active_club_name"],
      label: t("mem.activeClub"),
      render: (m) => m.active_club_name ?? "—",
      csv: (m) => m.active_club_name,
    },
    {
      key: "renew_club",
      fields: ["renew_club_name"],
      label: t("mem.renewClub"),
      render: (m) => m.renew_club_name ?? "—",
      csv: (m) => m.renew_club_name,
    },
    { key: "email", label: t("mem.email"), render: (m) => m.email ?? "—" },
    {
      key: "snr_account_number",
      label: t("mem.snrAccountNumber"),
      render: (m) => m.snr_account_number ?? "—",
    },
    { key: "card_name", label: t("mem.cardName"), render: (m) => m.card_name ?? "—" },
    { key: "unit_number", label: t("mem.unitNumber"), render: (m) => m.unit_number ?? "—" },
    {
      key: "building_name",
      label: t("mem.buildingName"),
      render: (m) => m.building_name ?? "—",
    },
    {
      key: "business_name",
      label: t("mem.businessName"),
      render: (m) => m.business_name ?? "—",
    },
    {
      key: "email_consent",
      label: t("mem.isEmailConsent"),
      render: (m) => (m.email_consent ? t("rp.yes") : t("rp.no")),
    },
    {
      key: "phone_consent",
      label: t("mem.isPhoneConsent"),
      render: (m) => (m.phone_consent ? t("rp.yes") : t("rp.no")),
    },
    {
      key: "only_important_updates",
      label: t("mem.onlyImportantUpdates"),
      render: (m) => (m.only_important_updates ? t("rp.yes") : t("rp.no")),
    },
    {
      // Keyed on the column the server names, so the audit column `ResourcePage`
      // appends to every screen dedupes against this one instead of doubling it.
      key: "created_by_id",
      fields: ["created_by_name"],
      label: t("mem.createdBy"),
      render: (m) => m.created_by_name ?? "—",
      csv: (m) => m.created_by_name,
    },
    {
      key: "updated_by_id",
      fields: ["updated_by_name"],
      label: t("mem.modifiedBy"),
      render: (m) => m.updated_by_name ?? "—",
      csv: (m) => m.updated_by_name,
    },
    { key: "updated_at", label: t("mem.modifiedAt"), render: (m) => fmtDate(m.updated_at) },
    {
      // Enrolment day. There is no `member_since` column — the day the row was
      // written is the day the member joined, and a second column saying so is
      // a second column that can disagree — so the label is the legacy one and
      // the field underneath is `created_at`. It is the list's default sort,
      // and `ix_members_created_at_id` is the index that serves it.
      key: "created_at",
      sortKey: "created_at",
      label: t("mem.memberSince"),
      render: (m) => fmtDate(m.created_at),
      csv: (m) => m.created_at,
    },
    {
      key: "suffix",
      label: t("mem.suffix"),
      render: (m) => label(SUFFIX(), m.suffix),
      csv: (m) => label(SUFFIX(), m.suffix),
    },
    {
      key: "employee_number",
      label: t("mem.employeeNumber"),
      render: (m) => m.employee_number ?? "—",
      csv: (m) => m.employee_number,
    },
    {
      key: "parent_account_number",
      label: t("mem.parentAccount"),
      render: (m) => m.parent_account_number ?? "—",
      csv: (m) => m.parent_account_number,
    },
    {
      key: "co_brand_tagging",
      label: t("mem.coBrandTagging"),
      render: (m) => m.co_brand_tagging ?? "—",
      csv: (m) => m.co_brand_tagging,
    },
  ],
  // Not a filter UI — `renderFilters` below takes that over — but what the
  // applied-filter chips are read through. A chip resolves its label and its
  // value against this list, so a filter missing from it renders as the raw
  // thing in the query: "Signup club 6", "Status expired".
  //
  // Derived from the Advanced Filter's own groups rather than restated, so a
  // filter added there is named here without anyone remembering to. Only the
  // two kinds that need resolving: `fk` turns an id into a club or a person,
  // `select` turns a stored enum value into the words the dropdown showed.
  filters: chipFilters(t),
  renderFilters: (values, onApply, onClose) => (
    <AdvancedFilter values={values} onApply={onApply} onClose={onClose} />
  ),
  // The saved sets live beside the filter button, so applying one is one
  // click rather than a trip through the drawer.

  fields: fields(t),
  toForm,
  newDefaults: (profile) => ({
    account_type: "primary",
    account_status: "active",
    signup_club_id: profile.club?.id.toString() ?? "",
    active_club_id: profile.club?.id.toString() ?? "",
  }),
  // Two solid buttons side by side leave the eye no answer about which one to
  // press, so only Add Member is filled; the CSV flows are secondary, and one
  // menu with two headings, rather than two menus, keeps the row to one line.
  toolbar: <CsvMenu />,
  // A place rather than an action, but still a button on the row: nothing here
  // hides behind a menu.
  menuExtra: (
    <Button asChild variant="secondary">
      <Link to="/member-activity-log">
        <History aria-hidden /> {t("page.activityLog")}
      </Link>
    </Button>
  ),
});

/** What the export button is asking, while it asks. */
interface ExportChoice {
  params: ListParams;
  onScreen: number;
  total: number;
  /** The total as the pager writes it — "10,000+" when the count stopped at the cap. */
  totalLabel: string;
  /** The count stopped at the cap, so `total` is a floor and not a number of rows. */
  capped: boolean;
  /** Every column, with the API fields behind it and whether the table shows it. */
  columns: { key: string; label: string; fields: string[]; selected: boolean }[];
  /** Write the rows already on screen, narrowed to these column keys. */
  writeScreen: (keys: string[]) => void;
}

function MembersPage() {
  const t = useTranslation();
  const [asking, setAsking] = useState<ExportChoice | null>(null);
  const base = useMemo(() => build(t), [t]);
  const config = useMemo(
    () => ({ ...base, exportAll: setAsking }) as ResourceConfig<Member>,
    [base],
  );
  return (
    <>
      <ResourcePage config={config} />
      {asking ? <ExportDialog choice={asking} onClose={() => setAsking(null)} /> : null}
    </>
  );
}

/**
 * Which members to download, asked only when it is a real question.
 *
 * The screen holding every match is the common case — a filter narrow enough
 * to read is a filter narrow enough to save — and then the button writes the
 * file from what it already has, with nothing asked and nothing fetched. This
 * opens when there is more, because "export" then means two different things
 * and guessing which would be wrong half the time.
 */
/** Keys on the list body that describe the page, not which members match. */
const PAGING_KEYS = new Set(["fields", "page", "size", "sort", "order"]);

/**
 * How many conditions are narrowing the list.
 *
 * Mirrors `MemberQuery.is_filtered` on the server, which is what actually
 * refuses an unfiltered export; this only decides what the button looks like.
 * `false` counts — "never expires: no" narrows the list — so this asks what is
 * set and never what is truthy. `status: "all"` does not: it is what the
 * dropdown reads before anybody chooses.
 */
export function activeFilters(params: ListParams): number {
  return Object.entries(params).filter(
    ([key, value]) =>
      !PAGING_KEYS.has(key) &&
      value !== null &&
      value !== undefined &&
      value !== "" &&
      !(Array.isArray(value) && value.length === 0) &&
      !(key === "status" && value === "all"),
  ).length;
}

const KB = 1024;
const MB = KB * KB;
/** Bytes as a person reads them. Below a megabyte the decimal is noise. */
const written = (bytes: number) =>
  bytes < MB
    ? `${Math.round(bytes / KB).toLocaleString()} KB`
    : `${(bytes / MB).toFixed(1)} MB`;

/** Elapsed as a clock: m:ss, which is how long a wait feels rather than 94800ms. */
const clock = (ms: number) => {
  const seconds = Math.floor(ms / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
};

/**
 * Which members to download, asked only when it is a real question.
 *
 * The screen holding every match is the common case — a filter narrow enough
 * to read is a filter narrow enough to save — and then the button writes the
 * file from what it already has, with nothing asked and nothing fetched. This
 * opens when there is more, because "export" then means two different things
 * and guessing which would be wrong half the time.
 *
 * It says what the file will hold before it is asked for — the columns, the
 * filters, how many members — and then what it is holding while it arrives.
 * A download with no size and no clock is a spinner, and a spinner over
 * something that legitimately takes two minutes is a button people press twice.
 */
function ExportDialog({ choice, onClose }: { choice: ExportChoice; onClose: () => void }) {
  const t = useTranslation();
  const filters = activeFilters(choice.params);
  /** Which columns the file will hold. Whatever Customize Columns has on, until
   *  someone says otherwise here — the screen's own choice is the right default,
   *  and this is for the download that wants less than a person reading does,
   *  or the one column nobody needs on screen but the file does. */
  const [picked, setPicked] = useState<string[]>(() =>
    choice.columns.filter((c) => c.selected).map((c) => c.key),
  );
  const [bytes, setBytes] = useState(0);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  /** Aborting the request closes the connection, and the endpoint asks
   *  `request.is_disconnected` between batches — so Cancel stops the server
   *  reading as well as the browser waiting, rather than leaving a cursor
   *  walking forty million rows for a file nobody will take. */
  const abort = useRef<AbortController | null>(null);

  // A clock only while there is something to time. Half a second is under what
  // a person reads as a lag and is a quarter of the repaints of a 60fps one.
  useEffect(() => {
    if (startedAt === null) return;
    const id = setInterval(() => setElapsed(Date.now() - startedAt), 500);
    return () => clearInterval(id);
  }, [startedAt]);

  const pickedFields = [
    ...new Set(choice.columns.filter((c) => picked.includes(c.key)).flatMap((c) => c.fields)),
  ];
  const allPicked = picked.length === choice.columns.length;
  const toggle = (key: string) =>
    setPicked((keys) => (keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]));

  const download = useMutation({
    mutationFn: async () => {
      // The list's own question, without the page: an export is every match.
      // `fields` is added back here and nowhere else — the list asks for every
      // column so that showing one costs no request, but the file holds the
      // columns the screen holds, which is also what the "this page" button
      // beside this one writes.
      const { page: _page, size: _size, ...filterParams } = choice.params;
      const controller = new AbortController();
      abort.current = controller;
      setBytes(0);
      setElapsed(0);
      setStartedAt(Date.now());
      const file = await api.post<Blob>(
        "/members/export",
        { ...filterParams, fields: pickedFields },
        {
          responseType: "blob",
          signal: controller.signal,
          // No deadline. The instance gives every other request a minute, which
          // an export of forty million members passes while the server is still
          // streaming it correctly — and axios aborting mid-stream is exactly
          // the "export does not work" this endpoint was reported for.
          timeout: 0,
          // The response is streamed, so it carries no Content-Length and there
          // is no percentage to be had. `loaded` is real though, and a size
          // climbing beside a clock is what says the wait is progress and not
          // a hang.
          onDownloadProgress: (event) => setBytes(event.loaded),
        },
      );
      const url = URL.createObjectURL(file.data);
      const link = Object.assign(document.createElement("a"), {
        href: url,
        download: csvName("member"),
      });
      // Appended, clicked, then removed: a detached anchor is not reliably
      // actionable, and revoking the URL in the same tick as the click cancels
      // the download the click just started.
      document.body.append(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
    onSuccess: onClose,
    onError: (error) => {
      setStartedAt(null);
      // A cancel is an answer, not a failure. It lands back on the picker so
      // the columns and filters are still there to adjust and try again.
      if (axios.isCancel(error)) {
        toast.info(t("mem.exportCancelled"));
        return;
      }
      toast.error(errorMessage(error, t("mem.exportFailed")));
    },
  });

  // Once it is running the dialog has one job, so it becomes one thing: the
  // columns are settled, the buttons would all be wrong to press, and the
  // picker underneath is a list of choices that no longer apply. Nothing
  // dismisses it — no X, no backdrop, no Escape — because every one of those
  // abandons the download, and a stray click beside a modal is the commonest
  // accident there is. It ends by finishing, or by failing back to the picker.
  if (download.isPending) {
    return (
      <Modal
        open
        onClose={() => undefined}
        size="sm"
        dismissible={false}
        title={t("mem.exportRunningTitle")}
        description={t("mem.exportRunningBody")}
        footer={
          <Button variant="secondary" onClick={() => abort.current?.abort()}>
            {t("mem.exportStop")}
          </Button>
        }
      >
        <div aria-live="polite" className="pb-2">
          <p className="text-title-3 text-fg tabular-nums">{written(bytes)}</p>
          <p className="text-fg-muted text-callout mt-1 tabular-nums">
            {t("mem.exportElapsed").replace("{elapsed}", clock(elapsed))}
          </p>
          {/* Indeterminate on purpose: the response is streamed with no length,
              and a bar filling against a guessed total is a bar that lies. */}
          <div className="bg-surface-sunken mt-4 h-1 overflow-hidden rounded-full">
            <div className="bg-accent animate-export-sweep h-full w-1/3 rounded-full" />
          </div>
          <p className="text-fg-muted text-footnote mt-3">{t("mem.exportKeepOpen")}</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      // Wider than the other notices: the column list is two columns of
      // checkboxes, and at `sm` the longer headers wrapped to three lines each.
      size="md"
      title={t("mem.exportTitle")}
      description={t("mem.exportBody")
        .replace("{onScreen}", choice.onScreen.toLocaleString())
        .replace("{total}", choice.totalLabel)}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="secondary"
            disabled={picked.length === 0}
            onClick={() => {
              choice.writeScreen(picked);
              onClose();
            }}
          >
            {t("mem.exportThisPage").replace("{count}", choice.onScreen.toLocaleString())}
          </Button>
          <Button
            // Unfiltered this is every member on record. The endpoint refuses
            // it too — a saved filter and a copied curl both reach it without
            // passing here — and this is the half that explains why.
            // A file with no columns is not a file. Both buttons say so.
            disabled={filters === 0 || picked.length === 0}
            onClick={() => download.mutate()}
          >
            {/* A capped total is a floor, not a count. "All 100,001" said the
                file had a hundred thousand and one members in it, when what it
                has is every member that matched — which may be twenty million.
                Better to promise all of them and name none. */}
            {choice.capped
              ? t("mem.exportEverythingUncounted")
              : t("mem.exportEverything").replace("{count}", choice.total.toLocaleString())}
          </Button>
        </>
      }
    >
      <dl className="text-callout grid grid-cols-[auto_1fr] gap-x-6 gap-y-2">
        <dt className="text-fg-muted">{t("mem.exportRows")}</dt>
        <dd className="text-fg tabular-nums">{choice.totalLabel}</dd>
        <dt className="text-fg-muted">{t("mem.exportFilters")}</dt>
        <dd className={filters === 0 ? "text-danger" : "text-fg tabular-nums"}>
          {filters === 0
            ? t("mem.exportNoFilters")
            : t("mem.exportFilterCount").replace("{count}", String(filters))}
        </dd>
      </dl>

      {/* The columns, as a choice rather than a paragraph. Names run as prose
          are a wall nobody reads and the one thing people open this to check;
          as switches it is the same information and answers "can I leave that
          one out" at the same time. Laid out like Customize Columns — the same
          list, the same order, the same ticks — because it is the same
          question asked about a file instead of a table, and a second shape
          for it would be a second thing to learn. */}
      <section className="mt-5">
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <p className="text-eyebrow text-fg-subtle">{t("mem.exportColumns")}</p>
          <button
            type="button"
            className="text-accent text-footnote hover:underline"
            onClick={() => setPicked(allPicked ? [] : choice.columns.map((c) => c.key))}
          >
            {allPicked ? t("mem.exportClearAll") : t("mem.exportSelectAll")}
          </button>
        </div>
        <div className="grid gap-1 sm:grid-cols-2">
          {choice.columns.map((column) => (
            <label
              key={column.key}
              className="hover:bg-surface-sunken flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5"
            >
              <Checkbox
                checked={picked.includes(column.key)}
                onChange={() => toggle(column.key)}
              />
              <span className="text-fg text-callout">{column.label}</span>
            </label>
          ))}
        </div>
        <p className="text-fg-muted text-footnote mt-2">
          {t("mem.exportColumnCount")
            .replace("{count}", String(picked.length))
            .replace("{total}", String(choice.columns.length))}
        </p>
      </section>

      <p className="text-fg-muted text-footnote mt-4">
        {filters === 0 ? t("mem.exportNeedsFilter") : t("mem.exportNote")}
      </p>
    </Modal>
  );
}

/**
 * The Detail View as a page: the way back, and the member's own body.
 *
 * No Refresh or Edit in the header — the body carries both already, a refresh
 * beside the card and a pencil on each section it can edit, and a second pair
 * above them was two of everything.
 */
function MemberView({ member, ctx }: { member: Member; ctx: DetailContext }) {
  const t = useTranslation();
  return (
    <>
      <PageHeader
        back={
          <button type="button" onClick={ctx.onClose} className={backLinkClass}>
            <BackArrow label={t("mem.singular")} />
          </button>
        }
        eyebrow={t("mem.singular")}
        title={
          <span className="flex flex-wrap items-center gap-2.5">
            <span className="min-w-0 truncate">
              {fullName(member) || t("mem.memberN").replace("{id}", String(member.id))}
            </span>
            <span className="bg-surface-inset text-fg-muted text-footnote rounded-full px-2 py-0.5 font-medium tabular-nums">
              {member.account_number}
            </span>
          </span>
        }
      />
      <MemberDetail id={member.id} embedded />
    </>
  );
}

// ---------------------------------------------------------------------------
// CSV menus: Batch Update (one file per action) and Bulk Upload (one per tier),
// each item with a sample template to start from.
// ---------------------------------------------------------------------------
interface CsvItem {
  key: string;
  label: TranslationKey;
  /** The route below /members that serves the template and takes the file. */
  path: string;
  /**
   * The header the file must carry, as the template writes it.
   *
   * Written here rather than read off the template: it is static, so fetching
   * it was a request made only to learn something already known. Matching is
   * by field — case, spaces and underscores folded — so the two drifting in
   * spelling cannot break a file, only a column genuinely going missing can.
   */
  columns: string[];
  /**
   * The example rows the download carries, when one row of the same value for
   * every column would not show what the column accepts. Left out otherwise:
   * `SAMPLE` fills a single row by column name.
   */
  sample?: string[][];
  /** The batch updates report themselves as they run; enrolment answers once. */
  streams?: boolean;
  hint: TranslationKey;
  /** `hint` only: the {extra} slot. */
  extra?: TranslationKey;
  file: string;
  /** What the result counts. */
  noun: "updated" | "created";
}

const BATCH: CsvItem[] = [
  {
    key: "volume_shopper",
    label: "mem.volumeShopper",
    path: "batch-update/volume-shopper",
    streams: true,
    columns: ["Member Card", "Volume Shopper"],
    sample: [
      ["4271582301", "yes"],
      ["4271582302", "no"],
    ],
    hint: "mem.hintYesNo",
    file: "member-bulk-update-volume-shopper-sample.csv",
    noun: "updated",
  },
  {
    key: "cancellation",
    label: "mem.cancellation",
    path: "batch-update/cancellation",
    streams: true,
    columns: ["Member Card", "Amendment Cancellation", "Reason"],
    sample: [
      ["4271582301", "Cancellation", "Incorrect Entry"],
      ["4271582302", "Cancellation", "Change of Mind"],
    ],
    hint: "mem.hintCancellation",
    file: "member-bulk-update-cancellation-sample.csv",
    noun: "updated",
  },
  {
    key: "valid_until",
    label: "mem.validUntil",
    path: "batch-update/valid-until",
    streams: true,
    columns: ["Member Card", "Date To"],
    sample: [
      ["4271582301", "9/1/2026"],
      ["4271582302", "10/15/2026"],
    ],
    hint: "mem.hintDate",
    file: "member-bulk-update-valid-until-sample.csv",
    noun: "updated",
  },
];

/**
 * What a column is filled in with in a downloaded template, by the header it
 * is written under. A column with nothing here is left blank, which is the
 * right answer for one like Suffix that most rows will not carry.
 */
const SAMPLE: Record<string, string> = {
  Prefix: "Mr",
  "First Name": "JUAN",
  "Middle Name": "SANTOS",
  "Last Name": "DELA CRUZ",
  "Date of Birth": "1/15/1990",
  Gender: "Male",
  "Mobile Number": "09171234567",
  Email: "juan@example.com",
  "Unit Number": "12B",
  "Building Name": "One Tower",
  "Address/Street": "123 Rizal St",
  City: "Quezon City",
  Barangay: "Bagumbayan",
  "Employee ID Number": "EMP-00123",
  "Company Name": "Acme Corp",
  "S&R Family Owner": "4271582301",
  "Email Consent": "yes",
  "Phone Consent": "yes",
  "Only Receive Important Updates": "yes",
  "Agree to Terms": "yes",
  "Agree to Personal Info Usage": "yes",
};

/** Every enrolment template starts and ends the same; only the middle differs. */
const UPLOAD_LEAD = [
  "Prefix",
  "First Name",
  "Middle Name",
  "Last Name",
  "Date of Birth",
  "Gender",
  "Mobile Number",
  "Email",
];
const UPLOAD_TAIL = [
  "Email Consent",
  "Phone Consent",
  "Only Receive Important Updates",
  "Agree to Terms",
  "Agree to Personal Info Usage",
  "Suffix",
];

const UPLOAD: CsvItem[] = (
  [
    [
      "employee",
      "mem.employee",
      "mem.hintEmployee",
      [
        "Unit Number",
        "Building Name",
        "Address/Street",
        "City",
        "Barangay",
        "Employee ID Number",
      ],
    ],
    ["affiliates", "mem.affiliates", "mem.hintAffiliates", ["Company Name"]],
    ["complimentary", "mem.complimentary", "mem.hintComplimentary", ["S&R Family Owner"]],
  ] as const
).map(([key, label, extra, middle]) => ({
  key,
  label,
  path: `upload/${key}`,
  columns: [...UPLOAD_LEAD, ...middle, ...UPLOAD_TAIL],
  hint: "mem.hintUpload",
  extra,
  file: `member-batch-upload-${key}-sample.csv`,
  noun: "created",
}));

/**
 * What each CSV column may hold, so the editor offers the control instead of a
 * blank box. Keyed by the column heading the templates use; a column not named
 * is free text. The choices are exactly the spellings the server parses —
 * `YES`/`NO`, `CANCELS`, the name enums — because a dropdown that offers what
 * the server refuses is a dropdown that manufactures rejections.
 */
const COLUMN_TYPES: Record<string, CellKind> = {
  "Member Card": { kind: "text", inputMode: "numeric" },
  "Volume Shopper": { kind: "options", options: ["yes", "no"], required: true },
  // The legacy template writes Cancellation; hand-typed files say yes. Both go.
  "Amendment Cancellation": {
    kind: "options",
    options: ["Cancellation", "yes"],
    required: true,
  },
  "Date To": { kind: "date" },
  "Date of Birth": { kind: "date" },
  Gender: {
    kind: "options",
    options: ["Male", "Female", "Other", "Prefer not to say"],
    required: true,
  },
  Prefix: { kind: "options", options: ["Mr", "Ms", "Mrs", "Dr", "Atty", "Engr"] },
  Suffix: { kind: "options", options: ["Sr", "Jr", "I", "II", "III", "IV", "V", "VI"] },
  "Mobile Number": { kind: "text", inputMode: "tel" },
  Email: { kind: "text", inputMode: "email" },
  "Email Consent": { kind: "options", options: ["yes", "no"] },
  "Phone Consent": { kind: "options", options: ["yes", "no"] },
  "Only Receive Important Updates": { kind: "options", options: ["yes", "no"] },
  "Agree to Terms": { kind: "options", options: ["yes", "no"] },
  "Agree to Personal Info Usage": { kind: "options", options: ["yes", "no"] },
  // `EMP-00123`: letters in it, so no numeric keyboard to fight.
  "Employee ID Number": { kind: "text" },
  // The primary's account number, despite the name.
  "S&R Family Owner": { kind: "text", inputMode: "numeric" },
};

interface CsvResult {
  updated?: number;
  created?: number;
  /** Batch updates only; a bulk enrolment answers with `created` and nothing else. */
  counts?: { total: number; succeeded: number; failed: number; unchanged: number };
  skipped: {
    row: number;
    member_card: string | null;
    error: string;
    /** The column the problem is in, as `columnKey` writes it — so the grid can
     *  point at the cell. Absent when the problem is the row as a whole. */
    field?: string | null;
    /** The row's own cells, so a rejected row can be corrected without the file. */
    values: Record<string, string>;
  }[];
}

/** The tiers a person can enrol from the list; each opens the enrolment flow. */
const TIERS: {
  tier: "member" | "affiliates" | "employee" | "complimentary";
  label: TranslationKey;
}[] = [
  { tier: "member", label: "mem.member" },
  { tier: "affiliates", label: "mem.affiliates" },
  { tier: "employee", label: "mem.employee" },
  { tier: "complimentary", label: "mem.complimentary" },
];

function AddMemberMenu() {
  const t = useTranslation();
  const { id, button, panelProps, close } = useAnchoredPopover(200);
  const navigate = useNavigate();
  return (
    <>
      <Button ref={button} popoverTarget={id}>
        <Plus aria-hidden /> {t("mem.addMember")}
        <ChevronDown aria-hidden />
      </Button>
      <div {...panelProps}>
        {TIERS.map((item) => (
          <button
            key={item.tier}
            type="button"
            onClick={() => {
              close();
              void navigate(`/members/new?tier=${encodeURIComponent(item.tier)}`);
            }}
            className={menuItemClass()}
          >
            {t(item.label)}
          </button>
        ))}
      </div>
    </>
  );
}

/** A function, so the page config above can name it before the item lists exist. */
/** The two CSV flows under one button, each its own heading in the menu. */
const CSV_GROUPS: { label: TranslationKey; items: CsvItem[] }[] = [
  { label: "mem.batchUpload", items: UPLOAD },
  { label: "mem.batchUpdate", items: BATCH },
];

/**
 * A top-layer popover anchored under a button: the toolbar never clips it.
 * The same shape serves every menu on this page.
 */
function useAnchoredPopover(minWidth: number) {
  const id = useId();
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const place = () => {
    const anchor = button.current?.getBoundingClientRect();
    const panel = menu.current;
    if (!anchor || !panel) return;
    // Right-aligned to its button when that keeps it on screen.
    const width = Math.max(anchor.width, minWidth);
    const left = Math.max(8, Math.min(anchor.left, window.innerWidth - width - 8));
    Object.assign(panel.style, {
      top: `${anchor.bottom + 4}px`,
      left: `${left}px`,
      width: `${width}px`,
    });
  };
  const panelProps = {
    ref: menu,
    id,
    popover: "auto" as const,
    onToggle: (event: React.ToggleEvent<HTMLDivElement>) =>
      event.newState === "open" && place(),
    className:
      "border-line bg-surface-raised animate-sheet fixed m-0 rounded-xl border p-1 shadow-[var(--shadow-float)]",
  };
  return { id, button, menu, panelProps, close: () => menu.current?.hidePopover() };
}

function CsvMenu() {
  const t = useTranslation();
  const { id, button, panelProps, close } = useAnchoredPopover(200);
  const [open, setOpen] = useState<{ group: TranslationKey; item: CsvItem } | null>(null);
  const allow = useCan();
  // An upload enrols, an update edits: each group shows to whoever may do that.
  const groups = CSV_GROUPS.filter((group) =>
    allow(group.label === "mem.batchUpload" ? "add__member" : "change__member"),
  );
  if (groups.length === 0) return null;

  return (
    <>
      <Button ref={button} variant="secondary" popoverTarget={id}>
        {t("mem.batch")} <ChevronDown aria-hidden />
      </Button>
      <div {...panelProps}>
        {groups.map((group) => (
          <div key={group.label} className="py-1">
            <p className="text-eyebrow text-fg-subtle px-3 pt-1 pb-1.5">{t(group.label)}</p>
            {group.items.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => {
                  close();
                  setOpen({ group: group.label, item });
                }}
                className={menuItemClass()}
              >
                {t(item.label)}
              </button>
            ))}
          </div>
        ))}
      </div>
      {open ? (
        <CsvDialog title={t(open.group)} item={open.item} onClose={() => setOpen(null)} />
      ) : null}
    </>
  );
}

/** How far a run has got, as the last frame told it. */
interface Progress {
  done: number;
  total: number;
  succeeded: number;
  unchanged: number;
  failed: number;
}

interface Finished {
  counts: { total: number; succeeded: number; failed: number; unchanged: number };
  updated: number;
  stopped: boolean;
}

interface AppliedRow {
  row: number;
  member_card: string;
  value: string;
}

/** A row on the receipt: what was in the file, and what became of it. */
interface ResultRow {
  /** The line in the posted file, header counted — what the server reported. */
  line: number;
  cells: string[];
  /** Why it did not go through. Absent on a row that did, or has not been tried. */
  error?: string;
  /** The column the error names, when it names one. */
  field?: string | null;
}

/**
 * The receipt, split the way a person acts on it: what is done, and what is
 * still theirs to deal with.
 *
 * Every `row` the server reports counts the header — it enumerates from 2 — so
 * `sent[row - 2]` is the row somebody typed. `sent` is what was *posted*, which
 * for "this page" is one page and not the sheet, and that is the whole reason
 * it is held: without it a result's line number indexes into the wrong array.
 *
 * A streaming run names the rows it applied. Bulk enrolment answers with a
 * count and the failures only, so there the successes are what is left once the
 * failures are taken out — the same set, arrived at from the other side.
 *
 * Pending is two things, because they are one job: rows that were refused, and
 * — only when a single page went — rows never tried. One of them being a
 * receipt and the other an omission nobody is told about is how a file ends up
 * half done.
 */
export function splitOutcome({
  header,
  sent,
  applied,
  skipped,
  streams,
  sheetRows,
  partial,
}: {
  header: string[];
  sent: string[][];
  applied: AppliedRow[];
  skipped: CsvResult["skipped"];
  streams: boolean;
  sheetRows: string[][];
  partial: boolean;
}): { succeeded: ResultRow[]; pending: ResultRow[] } {
  const failures = new Set(skipped.map((row) => row.row));
  const cellsFor = (line: number, fallback: Record<string, string>): string[] =>
    // A row refused before the grid ever held it still carries its own cells,
    // which is what `correctFailed` rebuilds from.
    sent[line - 2] ?? header.map((column) => fallback[columnKey(column)] ?? "");

  const succeeded = (
    streams
      ? applied.map((row) => row.row)
      : sent.map((_, index) => index + 2).filter((line) => !failures.has(line))
  ).map((line) => ({ line, cells: cellsFor(line, {}) }));

  const sentRows = new Set(sent);
  // One entry per line. The server reports at most one error per row — every
  // check either `continue`s or `setdefault`s — but this is the place a slip
  // would turn into damage: a line listed twice is the same member in the grid
  // twice, and a resubmit enrols them twice. Line 0 is exempt, because it is
  // not a line — it is "could not be tied to one", and two of those are two
  // different problems, not one row twice.
  const seenLines = new Set<number>();
  const oncePerLine = (row: { row: number }) =>
    row.row === 0 || !seenLines.has(row.row) ? (seenLines.add(row.row), true) : false;
  const pending = [
    ...skipped.filter(oncePerLine).map((row) => ({
      line: row.row,
      cells: cellsFor(row.row, row.values),
      error: row.error,
      ...(row.field != null ? { field: row.field } : {}),
    })),
    ...(partial
      ? sheetRows
          .map((cells, index) => ({ cells, line: index + 2 }))
          .filter((row) => !sentRows.has(row.cells))
      : []),
  ].sort((a, b) => a.line - b.line);

  return { succeeded, pending };
}

function CsvDialog({
  title,
  item,
  onClose,
}: {
  title: string;
  item: CsvItem;
  onClose: () => void;
}) {
  const t = useTranslation();
  const queryClient = useQueryClient();
  /** Whether anything was written, so closing knows the list behind is stale. */
  const landed = useRef(false);
  const close = () => {
    if (landed.current) void queryClient.invalidateQueries({ queryKey: ["/members"] });
    onClose();
  };
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The file read into memory: its header, and the rows under it. */
  const [sheet, setSheet] = useState<{ header: string[]; rows: string[][] } | null>(null);
  const [editing, setEditing] = useState(false);
  /** Which page of the review grid is shown; the file is read a hundred at a time. */
  const [page, setPage] = useState(1);
  // What the run has said so far. The failures are kept as state because the
  // banner counts them; the applied rows only feed the settle at the end of
  // `send`, which reads its own accumulators.
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [skipped, setSkipped] = useState<CsvResult["skipped"]>([]);
  const [finished, setFinished] = useState<Finished | null>(null);
  /** Why each row still in the grid was refused — and in which column, when
   *  the answer names one — aligned to `sheet.rows`. A row that was simply
   *  never sent has none. */
  const [rowErrors, setRowErrors] = useState<(RowError | undefined)[]>([]);
  /** Every row that has gone through, across runs: fix-and-resend sends only
   *  the remainder, so the record of what is already done has to live here. */
  const [done, setDone] = useState<string[][]>([]);
  /** Which half of the review screen is open. */
  const [tab, setTab] = useState<"pending" | "success">("pending");
  /** The Success tab's own page, so flipping tabs does not lose either place. */
  const [donePage, setDonePage] = useState(1);
  const running = useRef<AbortController | null>(null);

  // Checked against the columns the item declares. No request: the header is
  // static, so fetching the template to read it was a round trip to learn
  // something already known.
  const missing = sheet ? missingColumns(sheet.header, item.columns) : [];

  // Written here rather than fetched: the header is already declared above,
  // and a file this screen can spell out in full is not worth a round trip.
  const template = () => {
    const rows = item.sample ?? [item.columns.map((column) => SAMPLE[column] ?? "")];
    const csv = toCsv([item.columns, ...rows]);
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    Object.assign(document.createElement("a"), { href: url, download: item.file }).click();
    URL.revokeObjectURL(url);
  };

  const told = (count: number) =>
    t(item.noun === "updated" ? "mem.membersUpdated" : "mem.membersCreated").replace(
      "{count}",
      String(count),
    );

  /** The rows as they stand in the grid, posted as a file. */
  const send = async (rows: string[][], scope: "all" | "page") => {
    setError(null);
    setFinished(null);
    setProgress(null);
    setSkipped([]);
    // Held alongside the state, because the settle below runs in the same tick
    // the last frame arrives in and `applied`/`skipped` are a render behind.
    const gotApplied: AppliedRow[] = [];
    const gotSkipped: CsvResult["skipped"] = [];
    const name = file?.name ?? "batch.csv";
    const csv = toCsv([sheet?.header ?? item.columns, ...rows]);
    const body = new FormData();
    body.append("file", new File([csv], name, { type: "text/csv" }));
    setBusy(true);
    const controller = new AbortController();
    running.current = controller;
    try {
      if (!item.streams) {
        // Bulk enrolment still answers in one piece.
        const { data } = await api.post<CsvResult>(`/members/${item.path}`, body);
        gotSkipped.push(...data.skipped);
        setSkipped(data.skipped);
        setFinished({
          counts: {
            total: (data.created ?? 0) + data.skipped.length,
            succeeded: data.created ?? 0,
            failed: data.skipped.length,
            unchanged: 0,
          },
          updated: data.created ?? 0,
          stopped: false,
        });
        landed.current = landed.current || (data.created ?? 0) > 0;
        toast.success(told(data.created ?? 0));
        return;
      }
      for await (const frame of postEventStream(
        `${api.defaults.baseURL}/members/${item.path}`,
        body,
        controller.signal,
      )) {
        const payload: unknown = JSON.parse(frame.data);
        if (frame.event === "progress") {
          const chunk = payload as Progress & {
            applied: AppliedRow[];
            skipped: CsvResult["skipped"];
          };
          setProgress(chunk);
          if (chunk.applied.length) gotApplied.push(...chunk.applied);
          if (chunk.skipped.length) {
            gotSkipped.push(...chunk.skipped);
            setSkipped((held) => [...held, ...chunk.skipped]);
          }
          if (chunk.succeeded > 0) landed.current = true;
        } else if (frame.event === "done") {
          const last = payload as Finished;
          setFinished(last);
          toast.success(told(last.counts.succeeded));
        }
      }
    } catch (failure) {
      // An abort is the person pressing Stop; the run is already winding itself
      // up at the server's end, and its last frame may never arrive.
      if (controller.signal.aborted) setFinished(null);
      else if (failure instanceof StreamError)
        // A stream that never started answered with a problem+json, the same
        // shape every other error on this screen comes back as.
        setError((failure.problem as ProblemDetail | null)?.detail ?? t("mem.processFailed"));
      else setError(errorMessage(failure, t("mem.processFailed")));
    } finally {
      setBusy(false);
      running.current = null;
      // What a run leaves behind: the rows still to deal with, back in the grid
      // they were sent from. The ones that went through are gone — they are
      // done, and sending them again only asks the server to decide all over
      // again that there is nothing to do — so what is left is exactly the work
      // left, and the buttons under it count that. A refused row carries its
      // own cells, which is what puts it back without the file.
      const header = sheet?.header ?? item.columns;
      const { succeeded, pending } = splitOutcome({
        header,
        sent: rows,
        applied: gotApplied,
        skipped: gotSkipped,
        streams: item.streams ?? false,
        sheetRows: sheet?.rows ?? [],
        partial: scope === "page",
      });
      setSheet({ header, rows: pending.map((row) => row.cells) });
      setRowErrors(
        pending.map((row) =>
          row.error ? { message: row.error, field: row.field ?? null } : undefined,
        ),
      );
      setDone((held) => [...held, ...succeeded.map((row) => row.cells)]);
      setPage(1);
      // Every row in: the work is over, and the record of it is the answer.
      if (pending.length === 0) setTab("success");
    }
  };

  // A refusal the form can still act on — a file that would not parse, a
  // request the server turned down — belongs beside the field that caused it,
  // so it stays with the form rather than moving to the sending window.
  const errorNote = error ? (
    <p
      role="alert"
      className="bg-danger-soft text-danger text-callout flex items-start gap-2 rounded-lg px-3 py-2.5"
    >
      <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
      <span>{error}</span>
    </p>
  ) : null;

  const outcome = (
    <>
      {busy || progress ? (
        <ProgressNote progress={progress} busy={busy} onStop={() => running.current?.abort()} />
      ) : null}
      {errorNote}
    </>
  );

  // What the run came to, said once at the top of the grid it left behind.
  // The rows under it are the ones still to do, so the count of what went
  // through has nowhere else to be said.
  const banner = finished ? (
    <div
      role="status"
      className={cn(
        "text-callout rounded-lg px-3 py-2.5",
        skipped.length ? "bg-danger-soft text-danger" : "bg-success-soft text-success",
      )}
    >
      <span className="font-medium">{told(finished.counts.succeeded)}</span>
      {skipped.length ? (
        <span> {t("mem.csvRefused").replace("{count}", String(skipped.length))}</span>
      ) : null}
      {finished.stopped ? <span> {t("mem.csvStopped")}</span> : null}
      {finished.counts.unchanged ? (
        <span>
          {" "}
          {t("mem.csvUnchanged").replace("{count}", String(finished.counts.unchanged))}
        </span>
      ) : null}
    </div>
  ) : null;

  const reviewing = editing && sheet !== null && missing.length === 0;
  const { pages, shown } = pageSlice(sheet?.rows ?? [], page);

  const stopOrCancel = busy ? (
    <Button variant="secondary" onClick={() => running.current?.abort()}>
      {t("mem.csvStop")}
    </Button>
  ) : (
    <Button variant="secondary" onClick={close}>
      {t("common.cancel")}
    </Button>
  );

  // Sending is its own window, on the same reasoning as the export's. The form
  // above it has been answered and the editor behind it is a grid nobody should
  // be reading while rows are going in — and stacked under either one, a
  // receipt two thousand rows long pushed the buttons off the screen. Nothing
  // dismisses it while it is running: closing mid-file leaves a batch half
  // applied, which is the one outcome worth taking a click to avoid.
  if (busy) {
    return (
      <Modal
        open
        onClose={close}
        size="lg"
        dismissible={false}
        title={`${title} · ${t(item.label)}`}
        description={t("mem.csvSending")}
        footer={
          <Button variant="secondary" onClick={() => running.current?.abort()}>
            {t("mem.csvStop")}
          </Button>
        }
      >
        <div className="grid gap-4 pb-2">{outcome}</div>
      </Modal>
    );
  }

  // Correcting a file is work, not a question, and it wants the room its own
  // columns need: a grid of a dozen of them inside a dialog the width of a form
  // is a grid nobody can read, and a two-column file stretched across the
  // screen is worse. It sizes to the file, up to the viewport. Still a
  // `<dialog>`, for Escape and the focus trap.
  if (reviewing && sheet) {
    return (
      <Modal
        open
        onClose={close}
        size="fit"
        title={`${title} · ${t(item.label)}`}
        description={t("mem.csvReady")
          .replace("{rows}", String(sheet.rows.length))
          .replace("{name}", file?.name ?? "")}
        footer={
          <>
            {stopOrCancel}
            {/* Both count the Pending tab, whichever tab is open: what has
                gone through is done, and a resend carries the remainder only.
                Two scopes, because a long file is corrected a page at a time,
                and sending the whole thing to try one page is how you find out
                the other ninety-nine were fine. */}
            {pages > 1 ? (
              <Button
                variant="secondary"
                disabled={busy || shown.length === 0}
                onClick={() => void send(shown, "page")}
              >
                {t("mem.submitThisPage").replace("{count}", String(shown.length))}
              </Button>
            ) : null}
            <Button
              disabled={busy || sheet.rows.length === 0}
              loading={busy}
              loadingLabel={t("mem.processing")}
              onClick={() => void send(sheet.rows, "all")}
            >
              {t("mem.submitAll").replace("{count}", String(sheet.rows.length))}
            </Button>
          </>
        }
      >
        <div className="grid gap-4 pb-2">
          {banner}
          {errorNote}
          {/* The tabs appear once there is a record to split: before the first
              submit the whole file is pending and a tab strip over one tab is
              chrome with nothing to say. */}
          {done.length || finished ? (
            <Tabs
              label={t("mem.csvOutcome")}
              value={tab}
              onChange={setTab}
              items={[
                {
                  value: "pending" as const,
                  label: t("mem.csvPending"),
                  count: sheet.rows.length,
                },
                { value: "success" as const, label: t("mem.csvSucceeded"), count: done.length },
              ]}
            />
          ) : null}
          {tab === "success" && (done.length || finished) ? (
            done.length ? (
              <CsvEditor
                readOnly
                header={sheet.header}
                rows={done}
                page={donePage}
                onPageChange={setDonePage}
                busy={false}
                onChange={() => undefined}
              />
            ) : (
              <p className="text-fg-muted text-subhead py-6 text-center">
                {t("mem.csvNoneSucceeded")}
              </p>
            )
          ) : sheet.rows.length ? (
            <CsvEditor
              header={sheet.header}
              rows={sheet.rows}
              errors={rowErrors}
              types={COLUMN_TYPES}
              page={page}
              onPageChange={setPage}
              busy={busy}
              onChange={(rows) => setSheet({ header: sheet.header, rows })}
            />
          ) : (
            <p className="text-fg-muted text-subhead py-6 text-center">
              {t("mem.csvNonePending")}
            </p>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={close}
      size="lg"
      title={`${title} · ${t(item.label)}`}
      description={t("mem.csvDescription")
        .replace("{columns}", item.columns.join(", "))
        .replace("{hint}", t(item.hint).replace("{extra}", item.extra ? t(item.extra) : ""))}
      footer={
        <>
          {stopOrCancel}
          {/* The only way forward, so it is the filled one. Sending straight
              from here was the same file going in unseen, beside a button that
              shows it first — and the grid is where the row counts, the
              columns and the errors are legible. Every send now starts from a
              file somebody has looked at. */}
          <Button
            type="button"
            disabled={!sheet || missing.length > 0 || busy}
            onClick={() => setEditing(true)}
          >
            {t("mem.reviewAndEdit")}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 pb-2">
        <Button
          type="button"
          variant="secondary"
          className="justify-self-start"
          onClick={template}
        >
          <Download aria-hidden /> {t("mem.downloadTemplate")}
        </Button>

        <Field label={t("mem.csvFile")} htmlFor="csv-file">
          <Input
            id="csv-file"
            type="file"
            accept=".csv,text/csv"
            required
            disabled={busy}
            className="file:text-fg py-2 file:mr-3 file:rounded-md file:border-0 file:bg-transparent file:font-medium"
            onChange={(event) => {
              const chosen = event.target.files?.[0] ?? null;
              setFile(chosen);
              setSheet(null);
              setFinished(null);
              setSkipped([]);
              setProgress(null);
              setRowErrors([]);
              setDone([]);
              setTab("pending");
              setEditing(false);
              setPage(1);
              if (!chosen) return;
              void chosen.text().then((text) => {
                const [header = [], ...rows] = parseCsv(text);
                setSheet({ header, rows });
              });
            }}
          />
        </Field>

        {/* Checked here rather than by sending it: a missing column is a
            mistake about the file, and the answer to it is the template, not a
            round trip. */}
        {sheet && missing.length > 0 ? (
          <div
            role="alert"
            className="bg-danger-soft text-danger text-callout flex items-start gap-2 rounded-lg px-3 py-2.5"
          >
            <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
            <div className="grid gap-1.5">
              <span>{t("mem.csvMissingColumns").replace("{columns}", missing.join(", "))}</span>
              {/* Both headers spelled out: told only which column is missing,
                  somebody looking at a file whose header reads `Card No` has
                  no way to see what it should have read. */}
              <span className="text-footnote grid gap-0.5 opacity-80">
                <span>
                  {t("mem.csvExpectedHeader")}{" "}
                  <span className="font-mono">{item.columns.join(", ")}</span>
                </span>
                <span>
                  {t("mem.csvFileHeader")}{" "}
                  <span className="font-mono">{sheet.header.join(", ")}</span>
                </span>
              </span>
            </div>
          </div>
        ) : null}

        {sheet && missing.length === 0 && !editing ? (
          <p className="text-fg text-callout">
            {t("mem.csvReady")
              .replace("{rows}", String(sheet.rows.length))
              .replace("{name}", file?.name ?? "")}
          </p>
        ) : null}

        {errorNote}
      </div>
    </Modal>
  );
}

/**
 * How far the run has got, while it is getting there.
 *
 * A file of ten thousand rows is a minute of nothing to look at otherwise, and
 * a minute of nothing is indistinguishable from a minute of broken. Stop is
 * here rather than in the footer because it belongs to the thing it stops.
 */
function ProgressNote({
  progress,
  busy,
  onStop,
}: {
  progress: Progress | null;
  busy: boolean;
  onStop: () => void;
}) {
  const t = useTranslation();
  const done = progress?.done ?? 0;
  const total = progress?.total ?? 0;
  const share = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className="border-line grid gap-2 rounded-xl border p-3.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-fg text-callout tabular-nums">
          {total
            ? t("mem.csvProgress")
                .replace("{done}", String(done))
                .replace("{total}", String(total))
            : t("mem.processing")}
        </p>
        {busy ? (
          <Button variant="secondary" size="sm" onClick={onStop}>
            {t("mem.csvStop")}
          </Button>
        ) : null}
      </div>
      <div
        className="bg-surface-inset h-1.5 overflow-hidden rounded-full"
        role="progressbar"
        aria-valuenow={share}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="bg-accent h-full transition-[width] duration-300"
          style={{ width: `${share}%` }}
        />
      </div>
      {progress ? (
        <p className="text-fg-muted text-footnote tabular-nums">
          {t("mem.csvTally")
            .replace("{succeeded}", String(progress.succeeded))
            .replace("{failed}", String(progress.failed))}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Advanced Filter: grouped "filter by" rows, plus the person's saved sets.
// ---------------------------------------------------------------------------
type FilterKind = "select" | "fk" | "text" | "date" | "boolean" | "daterange";

interface FilterField {
  name: string;
  label: string;
  type: FilterKind;
  options?: Opt[];
  loadOptions?: (signal?: AbortSignal) => Promise<{ value: string | number; label: string }[]>;
  /** `fk` only: the /compact endpoint; `dependsOn` names the field whose value narrows it. */
  endpoint?: string;
  dependsOn?: string;
}

/** The query keys a field writes: a range is two, everything else one. */
const keysOf = (field: FilterField) =>
  field.type === "daterange" ? [`${field.name}_from`, `${field.name}_to`] : [field.name];

/**
 * The filters the endpoint reads as a list, so the drawer picks several.
 *
 * Mirrors `_LIST_FILTERS` on the server, which is the same set legacy leaves
 * un-`singleSelect`. A yes/no filter is not here: two of three answers is the
 * same as no filter at all. Neither are `city_id` and `barangay_id` — the
 * barangay dropdown narrows to one city, and `/barangays/compact` takes one.
 */
const MANY = new Set([
  "membership_type",
  "membership_tier",
  "account_status",
  "account_type",
  "gender",
  "acquisition_type",
  "union_bank_tag",
  "card_replacement_reason",
  "signup_club_id",
  "active_club_id",
  "renew_club_id",
  "created_by_id",
  "updated_by_id",
]);

/** What a field starts as when it is picked: a list filter starts empty-listed. */
const blankFor = (field: FilterField): string | string[] => (MANY.has(field.name) ? [] : "");

const STATUS_PRESET = (t: Translate) =>
  opts(t, [
    ["all", "mem.presetAll"],
    ["active", "mem.presetActive"],
    ["active_primary", "mem.presetActivePrimary"],
    ["active_supplementary", "mem.presetActiveSupplementary"],
    ["cancelled", "mem.presetCancelled"],
    ["expired", "mem.presetExpired"],
    ["blacklisted", "mem.presetBlacklisted"],
    ["pending_to_print", "mem.presetPendingToPrint"],
  ]);

interface FilterGroup {
  title: string;
  /** The "Filter by" select is the value itself, not a choice of field. */
  direct?: FilterField;
  fields: FilterField[];
}

/**
 * The Advanced Filter's fields, in the shape `ResourcePage` reads chips with.
 *
 * `fk`, `select` and `boolean`: the three whose stored value is not what a
 * person should read — an id, an enum member, and `true`. A date already says
 * itself, and a `daterange` writes two keys under names of its own, which the
 * chips humanise perfectly well.
 *
 * `dependsOn` is deliberately not carried over. It says which field narrows the
 * *choices* when picking, and `ResourcePage` skips a dependent filter when
 * resolving because it cannot know the parent — but a chip is resolved by id
 * against ids it has already asked for, and an id needs no parent. Kept, and
 * Barangay would read as a number.
 */
const chipFilters = (t: Translate): FilterDef[] =>
  groupsOf(t)
    .flatMap((group) => [...(group.direct ? [group.direct] : []), ...group.fields])
    .filter(
      (field) => field.type === "fk" || field.type === "select" || field.type === "boolean",
    )
    .map((field) => ({
      name: field.name,
      label: field.label,
      type: field.type as "fk" | "select" | "boolean",
      ...(field.endpoint ? { endpoint: field.endpoint } : {}),
      ...(field.options ? { options: field.options } : {}),
    }));

const groupsOf = (t: Translate): FilterGroup[] => [
  {
    title: t("mem.groupMemberStatus"),
    direct: {
      name: "status",
      label: t("mem.groupMemberStatus"),
      type: "select",
      options: STATUS_PRESET(t),
    },
    fields: [],
  },
  {
    title: t("mem.groupCardInformation"),
    fields: [
      { name: "issued_date", label: t("mem.cardIssuedDate"), type: "daterange" },
      { name: "valid_until", label: t("mem.cardExpiryDate"), type: "daterange" },
      {
        // The reasons are configured, so the options are the list itself and
        // the value is the text — which is what the amendment row stores.
        name: "card_replacement_reason",
        label: t("mem.cardReplacementHistory"),
        type: "select",
        loadOptions: async (signal) => {
          const { data } = await api.get<{ items: { reason: string }[] }>("/reasons", {
            params: { kind: "card_replacement", size: 200, sort: "reason" },
            ...(signal ? { signal } : {}),
          });
          return data.items.map((r) => ({ value: r.reason, label: r.reason }));
        },
      },
      { name: "is_print_ready", label: t("mem.isPrintReady"), type: "boolean" },
      { name: "no_expiry", label: t("mem.noExpiry"), type: "boolean" },
    ],
  },
  {
    title: t("mem.groupAccountDetails"),
    fields: [
      {
        name: "account_type",
        label: t("mem.accountType"),
        type: "select",
        options: ACCOUNT_TYPE(t),
      },
      {
        name: "membership_type",
        label: t("mem.membershipType"),
        type: "select",
        options: MEMBERSHIP_TYPE(t),
      },
      {
        name: "membership_tier",
        label: t("mem.membershipTier"),
        type: "select",
        options: TIER(t),
      },
    ],
  },
  {
    // Three clubs, as legacy has them. Where a member *lives* is the group
    // below: a club is a branch, and filing the two together sent people
    // looking for City under Club.
    title: t("mem.groupClubInformation"),
    fields: [
      {
        name: "signup_club_id",
        label: t("mem.signUpClub"),
        type: "fk",
        endpoint: "/clubs/compact",
      },
      {
        name: "renew_club_id",
        label: t("mem.renewClub"),
        type: "fk",
        endpoint: "/clubs/compact",
      },
      {
        name: "active_club_id",
        label: t("mem.activeClub"),
        type: "fk",
        endpoint: "/clubs/compact",
      },
    ],
  },
  {
    // Legacy asks this through the Member Status cascade's District option,
    // which is a club type listing locations. The flat form has no cascade to
    // hang it off, so the two address filters stand on their own.
    title: t("mem.groupAddress"),
    fields: [
      { name: "city_id", label: t("mem.city"), type: "fk", endpoint: "/cities/compact" },
      {
        name: "barangay_id",
        label: t("mem.barangay"),
        type: "fk",
        endpoint: "/barangays/compact",
        dependsOn: "city_id",
      },
    ],
  },
  {
    title: t("mem.groupMembershipActivity"),
    fields: [
      {
        name: "volume_shopper",
        label: t("mem.volumeShopperTag"),
        type: "select",
        options: opts(t, [
          ["true", "mem.withTag"],
          ["false", "mem.withoutTag"],
        ]),
      },
      {
        name: "union_bank_tag",
        label: t("mem.unionBankTag"),
        type: "select",
        options: CO_BRAND(t),
      },
    ],
  },
  {
    title: t("mem.groupMembershipProfile"),
    // Four, as legacy has them. "Email Address" here is the presence question —
    // who can be emailed at all — not a search for one; the search box above
    // the list is where a particular address is looked for.
    fields: [
      {
        name: "has_email",
        label: t("mem.emailAddress"),
        type: "select",
        options: opts(t, [
          ["true", "mem.withEmail"],
          ["false", "mem.withoutEmail"],
        ]),
      },
      {
        name: "has_mobile",
        label: t("mem.mobileNumber"),
        type: "select",
        options: opts(t, [
          ["true", "mem.withMobile"],
          ["false", "mem.withoutMobile"],
        ]),
      },
      { name: "dob", label: t("mem.birthdate"), type: "daterange" },
      { name: "gender", label: t("mem.gender"), type: "select", options: GENDER(t) },
    ],
  },
  {
    title: t("mem.groupPermission"),
    fields: [
      { name: "email_consent", label: t("mem.emailConsent"), type: "boolean" },
      { name: "phone_consent", label: t("mem.mobilePhoneConsent"), type: "boolean" },
    ],
  },
  {
    title: t("mem.groupSystemRelated"),
    fields: [
      { name: "created", label: t("mem.createdDate"), type: "daterange" },
      { name: "updated", label: t("mem.lastModifiedDate"), type: "daterange" },
      {
        name: "updated_by_id",
        label: t("mem.modifiedBy"),
        type: "fk",
        endpoint: "/users/compact",
      },
      {
        name: "acquisition_type",
        label: t("mem.sourceOfApplication"),
        type: "select",
        options: ACQUISITION(t),
      },
      {
        name: "created_by_id",
        label: t("mem.createdByFilter"),
        type: "fk",
        endpoint: "/users/compact",
      },
    ],
  },
];

interface SavedFilter {
  id: number;
  name: string;
  filters: FilterValues;
  owner: string | null;
  mine: boolean;
  created_at: string;
}

const SAVED_KEY = ["/members/saved-filters"];

/**
 * Whether a saved condition and the one on screen say the same thing.
 *
 * Not `===`: a list filter holds an array, and two arrays are never equal
 * however alike. Both sides are written sorted — `OptionSelect` sorts on every
 * pick — so joining is enough, and the comparison stays one line rather than a
 * deep-equality helper this file would need nowhere else.
 */
const sameValue = (mine: string | string[] | undefined, theirs: string | string[]): boolean =>
  (Array.isArray(mine) ? mine.join("\u0000") : (mine ?? "")) ===
  (Array.isArray(theirs) ? theirs.join("\u0000") : String(theirs));
//: How many saved sets the drawer shows before "View all". Two is a reminder
//: that they exist without turning the top of the drawer into a list.
const SAVED_STRIP_SHOWN = 2;
// `queryOptions` rather than a bare object: it is what types the query context,
// so `signal` is an AbortSignal here rather than an implicit any.
const savedFiltersQuery = queryOptions({
  queryKey: SAVED_KEY,
  queryFn: async ({ signal }) =>
    (await api.get<SavedFilter[]>("/members/saved-filters", { signal })).data,
});

/** What a saved set says about itself: how many conditions, and when. */
function savedSummary(row: SavedFilter, t: (key: TranslationKey) => string): string {
  const count = Object.values(row.filters).filter(isSet).length;
  return `${t("mem.filtersCount").replace("{count}", String(count))} · ${day(row.created_at)}`;
}

/**
 * Two of them, at the top of the drawer.
 *
 * Saved sets and the conditions they hold are the same subject, and they were
 * two buttons in the toolbar — one to read a set, another to write one. Reading
 * happens here now, above the conditions it would replace. Two, because a
 * strip is a reminder of what is there; the rest are behind View all, which is
 * the list as it was.
 */
function SavedFilterStrip({
  values,
  onLoad,
  onApply,
  onViewAll,
}: {
  values: FilterValues;
  onLoad: (row: SavedFilter) => void;
  onApply: (row: SavedFilter) => void;
  onViewAll: () => void;
}) {
  const t = useTranslation();
  const saved = useQuery(savedFiltersQuery);
  const rows = saved.data ?? [];
  if (rows.length === 0) return null;
  const applied = (row: SavedFilter) =>
    Object.entries(row.filters).every(([key, value]) => sameValue(values[key], value)) &&
    Object.entries(values).every(([key, value]) => !isSet(value) || key in row.filters);

  return (
    <section className="border-line mb-1 border-b pb-4">
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-eyebrow text-fg-subtle">{t("mem.savedFilters")}</p>
        <button
          type="button"
          onClick={onViewAll}
          className="text-accent text-footnote font-medium underline-offset-4 hover:underline"
        >
          {t("mem.viewAllSaved").replace("{count}", String(rows.length))}
        </button>
      </div>
      <ul className="grid gap-2">
        {rows.slice(0, SAVED_STRIP_SHOWN).map((row) => (
          <li
            key={row.id}
            className="border-line flex items-center justify-between gap-3 rounded-xl border px-3 py-2"
          >
            {/* The name reads the set into the drawer, so it can be looked at
                and changed before it is applied. Apply is the shortcut past
                that, which is what the old menu did. */}
            <button
              type="button"
              onClick={() => onLoad(row)}
              className="min-w-0 flex-1 text-left"
            >
              <span className="text-fg text-callout block truncate font-medium">
                {row.name}
              </span>
              <span className="text-fg-muted text-footnote block tabular-nums">
                {savedSummary(row, t)}
              </span>
            </button>
            <Button
              size="sm"
              variant={applied(row) ? "secondary" : "primary"}
              disabled={applied(row)}
              onClick={() => onApply(row)}
            >
              {applied(row) ? t("mem.applied") : t("mem.apply")}
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The person's saved sets, beside the filter button: what each one holds, when
 * it was saved, and Apply. Renaming and deleting happen here as well as in the
 * drawer, since this is where the sets are read.
 */
function SavedFiltersMenu({
  open,
  onClose,
  values,
  onApply,
  onEdit,
}: {
  open: boolean;
  onClose: () => void;
  values: FilterValues;
  onApply: (values: FilterValues) => void;
  /** Read the set into the drawer this is opened from, rather than a second one. */
  onEdit: (row: SavedFilter) => void;
}) {
  const t = useTranslation();
  const queryClient = useQueryClient();
  const close = onClose;
  const saved = useQuery(savedFiltersQuery);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: SAVED_KEY });
  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/members/saved-filters/${id}`),
    onSuccess: () => {
      toast.success(t("mem.filterDeleted"));
      void invalidate();
    },
    onError: (error) => toast.error(errorMessage(error, t("mem.filterDeleteFailed"))),
  });
  const rows = saved.data ?? [];
  const same = (row: SavedFilter) =>
    Object.entries(row.filters).every(([key, value]) => sameValue(values[key], value)) &&
    Object.entries(values).every(([key, value]) => !isSet(value) || key in row.filters);

  return (
    <>
      {/* Centred: a set is read before it is applied, and a list of them wants
          the room a popover under a toolbar has not got. */}
      <Modal
        open={open}
        onClose={close}
        size="md"
        title={t("mem.savedFilters")}
        description={t("mem.savedFiltersHint")}
      >
        {rows.length === 0 ? (
          <EmptyState
            title={t("mem.noSavedFilters")}
            description={t("mem.noSavedFiltersBody")}
            className="py-6"
          />
        ) : (
          <ul className="divide-line divide-y">
            {rows.map((row) => {
              const entries = Object.values(row.filters).filter(isSet);
              return (
                <li key={row.id} className="py-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-fg text-callout truncate font-medium">
                        {row.name}
                        {row.mine ? null : (
                          <span className="text-fg-subtle font-normal">
                            {" "}
                            · {row.owner ?? t("mem.someone")}
                          </span>
                        )}
                      </p>
                      <p className="text-fg-muted text-footnote mt-0.5 tabular-nums">
                        {t("mem.filtersCount").replace("{count}", String(entries.length))} ·{" "}
                        {day(row.created_at)}
                      </p>
                    </div>
                    <span className="flex shrink-0 items-center gap-0.5">
                      <button
                        type="button"
                        title={t("common.edit")}
                        onClick={() => {
                          close();
                          onEdit(row);
                        }}
                        className="text-fg-subtle hover:bg-surface-inset hover:text-fg grid size-7 place-items-center rounded-lg transition-colors"
                      >
                        <Pencil className="size-3.5" aria-hidden />
                        <span className="sr-only">
                          {t("mem.editName").replace("{name}", row.name)}
                        </span>
                      </button>
                      <button
                        type="button"
                        title={t("common.delete")}
                        onClick={() => remove.mutate(row.id)}
                        className="text-fg-subtle hover:bg-danger-soft hover:text-danger grid size-7 place-items-center rounded-lg transition-colors"
                      >
                        <Trash2 className="size-3.5" aria-hidden />
                        <span className="sr-only">
                          {t("mem.deleteName").replace("{name}", row.name)}
                        </span>
                      </button>
                      <Button
                        size="sm"
                        className="ml-1"
                        variant={same(row) ? "secondary" : "primary"}
                        disabled={same(row)}
                        onClick={() => {
                          close();
                          onApply(row.filters);
                        }}
                      >
                        {same(row) ? t("mem.applied") : t("mem.apply")}
                      </Button>
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Modal>
    </>
  );
}

/**
 * One field, so it is a modal rather than a browser prompt — the same shape
 * whether the set is new or being renamed.
 */
function FilterNameModal({
  naming,
  loading,
  onClose,
  onSubmit,
}: {
  naming: { id?: number; name: string } | null;
  loading: boolean;
  onClose: () => void;
  onSubmit: (name: string) => void;
}) {
  const t = useTranslation();
  const [name, setName] = useState("");
  // The field starts with the current name each time the modal opens.
  const [openedFor, setOpenedFor] = useState<typeof naming>(null);
  if (naming !== openedFor) {
    setOpenedFor(naming);
    setName(naming?.name ?? "");
  }
  return (
    <Modal
      open={naming !== null}
      onClose={onClose}
      size="sm"
      title={naming?.id ? t("mem.renameFilter") : t("mem.saveThisFilter")}
      description={naming?.id ? undefined : t("mem.saveHint")}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="filter-name" loading={loading}>
            {t("rp.save")}
          </Button>
        </>
      }
    >
      <form
        id="filter-name"
        className="pb-2"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = name.trim();
          if (!trimmed) return;
          onSubmit(trimmed);
          onClose();
        }}
      >
        <Field label={t("mem.name")} htmlFor="filter-name-input">
          <Input
            id="filter-name-input"
            autoFocus
            required
            maxLength={80}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
      </form>
    </Modal>
  );
}

function AdvancedFilter({
  values,
  onApply,
  onClose,
  editing,
}: {
  values: FilterValues;
  onApply: (values: FilterValues) => void;
  onClose: () => void;
  /** A saved set being changed: the drawer opens on its conditions and saves back to it. */
  editing?: SavedFilter;
}) {
  const t = useTranslation();
  const GROUPS = useMemo(() => groupsOf(t), [t]);
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<FilterValues>(values);
  /** The set's name while one is being edited; it saves with the conditions. */
  const [name, setName] = useState(editing?.name ?? "");
  /** Extra "Select field" rows per group, beyond the one every group shows when empty. */
  const [blanks, setBlanks] = useState<Record<string, number>>({});
  /** Naming a set, either a new one or a rename — the same one field either way. */
  const [naming, setNaming] = useState<{ id?: number; name: string } | null>(null);
  /** The full list, opened from the strip above the conditions. */
  const [showSaved, setShowSaved] = useState(false);
  /** Which set the drawer is changing, if any. A prop to begin with; the list
   *  above can hand it another, and the footer then saves back to that one. */
  const [editingSet, setEditingSet] = useState<SavedFilter | undefined>(editing);
  const set = (name: string, value: string | string[]) =>
    setDraft((d) => ({ ...d, [name]: value }));
  const clearKeys = (keys: string[]) =>
    setDraft((d) => {
      const next = { ...d };
      for (const key of keys) delete next[key];
      return next;
    });
  const addBlank = (title: string, delta: number) =>
    setBlanks((b) => ({ ...b, [title]: Math.max(0, (b[title] ?? 0) + delta) }));

  const invalidate = () => queryClient.invalidateQueries({ queryKey: SAVED_KEY });
  const save = useMutation({
    mutationFn: async ({ id, name }: { id?: number; name: string }) => {
      const body = {
        name,
        filters: Object.fromEntries(Object.entries(draft).filter(([, v]) => isSet(v))),
      };
      return id
        ? api.patch(`/members/saved-filters/${id}`, body)
        : api.post("/members/saved-filters", body);
    },
    onSuccess: (_, { id }) => {
      toast.success(t("mem.filterSaved"));
      void invalidate();
      if (id) onClose();
    },
    onError: (error) => toast.error(errorMessage(error, t("mem.filterSaveFailed"))),
  });
  const rename = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      api.patch(`/members/saved-filters/${id}`, { name }),
    onSuccess: () => void invalidate(),
    onError: (error) => toast.error(errorMessage(error, t("mem.renameFailed"))),
  });

  const active = (group: FilterGroup) =>
    group.fields.filter((f) => keysOf(f).some((k) => k in draft));
  /** Pick a field for a row: "" is All, which drops the group's filters. */
  const choose = (group: FilterGroup, from: FilterField | null, name: string) => {
    if (from) clearKeys(keysOf(from));
    else addBlank(group.title, -1);
    if (!name) {
      clearKeys(group.fields.flatMap(keysOf));
      return;
    }
    const field = group.fields.find((f) => f.name === name);
    if (field) for (const key of keysOf(field)) set(key, blankFor(field));
  };

  const chosen = Object.values(draft).filter(isSet).length;

  return (
    <>
      {/* Centred, like every other screen's filter dialog. It was a drawer, on
          the reasoning that seeing the list it narrows is most of the point —
          but there are twenty-odd conditions across eight groups here, and a
          panel that tall against the side of a table is neither reading the
          table nor giving the conditions the room they need. */}
      <Modal
        open
        onClose={onClose}
        size="lg"
        title={
          editingSet
            ? t("mem.editFilter").replace("{name}", editingSet.name)
            : t("mem.advancedFilter")
        }
        description={
          chosen === 0
            ? t("mem.nothingApplied")
            : t("mem.conditionsSet").replace("{count}", String(chosen))
        }
        footer={
          <>
            {editingSet ? (
              <Button
                variant="ghost"
                className="mr-auto"
                disabled={chosen === 0 || name.trim() === ""}
                loading={save.isPending}
                onClick={() => save.mutate({ id: editingSet.id, name: name.trim() })}
              >
                {t("mem.saveChanges")}
              </Button>
            ) : (
              <Button
                variant="ghost"
                className="mr-auto"
                disabled={chosen === 0}
                onClick={() => setNaming({ name: "" })}
              >
                {t("mem.saveSet")}
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => {
                setDraft({});
                setBlanks({});
              }}
            >
              {t("mem.reset")}
            </Button>
            <Button onClick={() => onApply(draft)}>{t("mem.apply")}</Button>
          </>
        }
      >
        {editingSet ? (
          <section className="mb-6">
            <Field label={t("mem.name")} htmlFor="af-name">
              <Input
                id="af-name"
                required
                maxLength={80}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </Field>
          </section>
        ) : null}

        {/* Before the conditions, because a set is chosen before it is edited. */}
        {editingSet ? null : (
          <SavedFilterStrip
            values={draft}
            onLoad={(row) => {
              setDraft(row.filters);
              setBlanks({});
            }}
            onApply={(row) => onApply(row.filters)}
            onViewAll={() => setShowSaved(true)}
          />
        )}

        {/* The list as it was — read, rename, delete, apply — opened from the
            strip rather than from a second button in the toolbar. Editing one
            reads it into this drawer instead of opening another. */}
        <SavedFiltersMenu
          open={showSaved}
          onClose={() => setShowSaved(false)}
          values={draft}
          onApply={(next) => onApply(next)}
          onEdit={(row) => {
            setDraft(row.filters);
            setName(row.name);
            setEditingSet(row);
            setBlanks({});
          }}
        />

        <section>
          <p className="text-eyebrow text-fg-subtle mb-2">{t("mem.membershipSince")}</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("mem.from")} htmlFor="af-since-from">
              <Input
                id="af-since-from"
                type="date"
                value={draft.created_from ?? ""}
                onChange={(e) => set("created_from", e.target.value)}
              />
            </Field>
            <Field label={t("mem.to")} htmlFor="af-since-to">
              <Input
                id="af-since-to"
                type="date"
                value={draft.created_to ?? ""}
                onChange={(e) => set("created_to", e.target.value)}
              />
            </Field>
          </div>
        </section>

        {GROUPS.map((group) => {
          const rows = active(group);
          const blankCount = group.direct
            ? 0
            : Math.max(blanks[group.title] ?? 0, rows.length === 0 ? 1 : 0);
          return (
            <section key={group.title} className="hairline-t mt-5 pt-5">
              <div className="flex items-center justify-between gap-3">
                <p className="text-eyebrow text-fg-subtle">{group.title}</p>
                {group.direct ? null : (
                  <Button variant="ghost" size="xs" onClick={() => addBlank(group.title, 1)}>
                    <Plus aria-hidden /> {t("mem.addRow")}
                  </Button>
                )}
              </div>

              {group.direct ? (
                <FilterRow onClear={() => clearKeys([group.direct!.name])}>
                  <OptionSelect
                    value={draft[group.direct.name] ?? ""}
                    onChange={(next) => set(group.direct!.name, next)}
                    choices={group.direct.options ?? []}
                    placeholder={t("mem.selectField")}
                  />
                </FilterRow>
              ) : null}

              {rows.map((field) => (
                <FilterRow key={field.name} onClear={() => clearKeys(keysOf(field))}>
                  <OptionSelect
                    value={field.name}
                    onChange={(next) => choose(group, field, next)}
                    choices={group.fields
                      .filter((f) => f === field || !rows.includes(f))
                      .map((f) => ({ value: f.name, label: f.label }))}
                    placeholder={t("mem.all")}
                  />
                  <FilterValue field={field} draft={draft} onChange={set} />
                </FilterRow>
              ))}

              {Array.from({ length: blankCount }, (_, i) => (
                <FilterRow key={`blank-${i}`} onClear={() => addBlank(group.title, -1)}>
                  <OptionSelect
                    value=""
                    onChange={(next) => choose(group, null, next)}
                    choices={group.fields
                      .filter((f) => !rows.includes(f))
                      .map((f) => ({ value: f.name, label: f.label }))}
                    placeholder={t("mem.selectField")}
                  />
                </FilterRow>
              ))}
            </section>
          );
        })}
      </Modal>

      <FilterNameModal
        naming={naming}
        loading={save.isPending || rename.isPending}
        onClose={() => setNaming(null)}
        onSubmit={(name) => {
          if (naming?.id) rename.mutate({ id: naming.id, name });
          else save.mutate({ name });
        }}
      />
    </>
  );
}

/** "Filter by" with its clear button, then whatever the row holds. */
function FilterRow({ onClear, children }: { onClear: () => void; children: ReactNode }) {
  const t = useTranslation();
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between">
        <span className="text-fg-muted text-subhead">{t("mem.filterBy")}</span>
        <button
          type="button"
          onClick={onClear}
          className="text-fg-subtle hover:text-fg grid size-6 place-items-center"
        >
          <X className="size-3.5" aria-hidden />
          <span className="sr-only">{t("mem.removeFilter")}</span>
        </button>
      </div>
      <div className="mt-1 grid gap-2">{children}</div>
    </div>
  );
}

/** The value control for a chosen field: data according to its kind. */
function FilterValue({
  field,
  draft,
  onChange,
}: {
  field: FilterField;
  draft: FilterValues;
  onChange: (name: string, value: string | string[]) => void;
}): ReactNode {
  const t = useTranslation();
  const remote = useQuery({
    queryKey: ["options", "filter", field.name],
    queryFn: ({ signal }) => field.loadOptions?.(signal) ?? Promise.resolve([]),
    enabled: Boolean(field.loadOptions),
    staleTime: 300_000,
  });
  if (field.type === "daterange") {
    const from = `${field.name}_from`;
    const to = `${field.name}_to`;
    return (
      <div className="grid grid-cols-2 gap-3">
        <Input
          type="date"
          aria-label={`${field.label} from`}
          value={String(draft[from] ?? "")}
          onChange={(e) => onChange(from, e.target.value)}
        />
        <Input
          type="date"
          aria-label={`${field.label} to`}
          value={String(draft[to] ?? "")}
          onChange={(e) => onChange(to, e.target.value)}
        />
      </div>
    );
  }
  if (field.type === "fk") {
    // A dependent list narrows to one parent, so the parent is read as one
    // value — which is why `city_id` stays a single choice.
    const chosen = field.dependsOn ? draft[field.dependsOn] : undefined;
    const parent = Array.isArray(chosen) ? (chosen[0] ?? "") : (chosen ?? "");
    const waiting = Boolean(field.dependsOn) && parent === "";
    return (
      <FkSelect
        endpoint={field.endpoint ?? ""}
        value={draft[field.name] ?? blankFor(field)}
        onChange={(next) => onChange(field.name, next)}
        params={field.dependsOn ? { [field.dependsOn]: parent } : undefined}
        disabled={waiting}
        placeholder={
          waiting
            ? t("rp.chooseFirst").replace(
                "{parent}",
                field.dependsOn?.replace(/_id$/, "") ?? "",
              )
            : t("rp.selectDots")
        }
      />
    );
  }
  if (field.type === "select" || field.type === "boolean") {
    const options: Opt[] =
      field.type === "boolean"
        ? [
            { value: "true", label: t("rp.yes") },
            { value: "false", label: t("rp.no") },
          ]
        : (field.options ??
          (remote.data ?? []).map((o) => ({ value: String(o.value), label: o.label })));
    return (
      <OptionSelect
        value={draft[field.name] ?? blankFor(field)}
        onChange={(next) => onChange(field.name, next)}
        choices={options}
        placeholder={t("rp.selectDots")}
      />
    );
  }
  const typed = draft[field.name];
  return (
    <Input
      type={field.type}
      aria-label={field.label}
      value={typeof typed === "string" ? typed : ""}
      onChange={(e) => onChange(field.name, e.target.value)}
    />
  );
}

export default MembersPage;
